import { Component, Inject, Injector, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { EMPTY, forkJoin, Observable, of, ReplaySubject, Subject } from "rxjs";
import { catchError, filter, finalize, map, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { SampleSurvey } from "src/app/models/sampleSurvey.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { SampleSurveyTypeService } from "src/app/services/sample-survey-type.service";
import { SampleSurveyService } from "src/app/services/sample-survey.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import { Lookup } from "src/app/models/lookup.model";
import {
    DateFormField,
    FileAttachmentsField,
    FormField,
    HierarchicalSelectionFormField,
    SingleSelectionFormField,
    TextAreaFormField,
    TextFormField,
} from "src/app/shared/form/formField.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    buildEmployeeOptionConfig,
    EMPLOYEE_BASE_LABEL_MAP,
} from "src/app/shared/form/employee-field-helpers";
import { isCustomFieldKey } from "src/app/shared/form/custom-field-helpers";
import { FileAttachmentService } from "src/app/services/file-attachment.service";
import { FileAttachment } from "src/app/models/fileAttachment.model";
import { AssetTypeEnum } from "src/app/enums/assetType.enum";
import { AttachmentsDialogComponent } from "../attachments-dialog/attachments-dialog.component";
import { CustomField } from "src/app/models/customField.model";
import { CustomObjectService } from "src/app/services/customObject.service";
import { CustomEntityService } from "src/app/services/customEntity.service";
import { HttpHeaders } from "@angular/common/http";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { CustomEntity } from "src/app/models/customEntity.model";
import {
    CustomFieldTypeEnum,
} from "src/app/enums/customFieldType.enum";
import { SampleSurveyType } from "src/app/models/sampleSurveyType.model";
import { EndpointsTableComponent } from "src/app/components/endpoints-table/endpoints-table.component";
import { SurveyTemplateService } from "src/app/services/surveyTemplate.service";
import { SurveyDialogTemplateEditData, SurveyTemplate, SurveyTemplateSettings } from "src/app/models/surveyTemplate.model";
import { SaveTemplateMode, SAVE_TEMPLATE_MODE_LABELS } from "src/app/enums/saveTemplateMode.enum";
import { TemplateSaveDialogComponent } from "src/app/common-components/template-save-dialog/template-save-dialog.component";
import { TemplateSaveDialogData, TemplateSaveDialogResult } from "src/app/common-components/template-save-dialog/template-save-dialog.model";
import { ConfirmDialogComponent, ConfirmDialogData } from "src/app/shared/confirmDialog/confirmDialog.component";
import { CreatePayload } from "src/app/shared/grid/crud.service";
import { ILookup } from "src/app/shared/form/lookup.model";
import { TemplateFieldState } from "src/app/models/templateFieldState.model";
import { ResolvedSurveyTemplate, SurveyTemplateField } from "./survey-dialog.model";
import { EDIT_SURVEY_TEMPLATE_ID_KEY, SURVEY_TEMPLATE_DATE_FIELDS, SURVEY_TEMPLATE_FIELDS } from "./survey-dialog.constants";
import { AuthService } from "src/app/services/auth.service";
import { PermissionService } from "src/app/services/permission.service";
import { canUseSurveyTemplates } from "src/app/shared/helpers/template-kind.helper";

@Component({
  standalone: false,
    selector: "app-survey-dialog",
    templateUrl: "./survey-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/form/form.component.scss",
        "./survey-dialog.component.scss",
    ],
})
export class SurveyDialogComponent
    extends FormComponent<SampleSurvey>
    implements OnInit, OnDestroy
{
    organizationLevels!: OrganizationLevel[];
    fields: FormField[] = [];
    sampleSurveyTypes!: Lookup[];
    attachedFiles: FileAttachment[] = [];
    customObjectsList: CustomField[] = [];
    employees: Lookup[] = [];
    shownFields: string[] = [];
    coId!: number | string;
    availableTemplates: Lookup[] = [];
    selectedTemplateId: string | null = null;
    templateFieldStates = new Map<string, TemplateFieldState>();
    isDefaultTemplateBannerVisible = false;
    isTemplateDirtyState = false;
    saveTemplateMode = SaveTemplateMode.Create;
    saveTemplateLabel = SAVE_TEMPLATE_MODE_LABELS[SaveTemplateMode.Create];
    isSavingTemplate = false;
    readonly isTemplateEdit: boolean;
    readonly canUseTemplates: boolean;

    private readonly destroy$ = new Subject<void>();
    private readonly employeesReady$ = new ReplaySubject<void>(1);
    private readonly templateFilledFields = new Set<string>();
    private readonly templateEditedFields = new Set<string>();
    private readonly editTemplateId: string | null;
    private readonly requestedByKey: SurveyTemplateField =
        nameOf<SurveyTemplateSettings>((x) => x.requestedById);
    private readonly attachmentsKey: SurveyTemplateField =
        nameOf<SurveyTemplateSettings>((x) => x.fileAttachmentIds);
    private appliedTemplate: SurveyTemplate | null = null;
    private editingTemplate: SurveyTemplate | null = null;
    private templateBaseline: string | null = null;
    private templateSelectionRequestId = 0;

    constructor(
        dialogRef: MatDialogRef<SurveyDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: IFormDialogData | SurveyDialogTemplateEditData,
        sampleSurveyService: SampleSurveyService,
        private organizationLevelService: OrganizationLevelService,
        private sampleSurveyTypeService: SampleSurveyTypeService,
        private fileAttachmentService: FileAttachmentService,
        private dialog: MatDialog,
        protected injector: Injector,
        private customObjectService: CustomObjectService,
        private customEntityService: CustomEntityService,
        private surveyTemplateService: SurveyTemplateService,
    ) {
        super(
            dialogRef,
            sampleSurveyService,
            EDIT_SURVEY_TEMPLATE_ID_KEY in data ? { id: "" } : data,
            "Survey",
            injector,
        );
        this.editTemplateId =
            EDIT_SURVEY_TEMPLATE_ID_KEY in data ? data.editSurveyTemplateId : null;
        this.isTemplateEdit = this.editTemplateId != null;
        this.canUseTemplates =
            this.isAdd &&
            !this.isTemplateEdit &&
            canUseSurveyTemplates(
                this.injector.get(AuthService),
                this.injector.get(PermissionService),
            );
        if (this.isTemplateEdit) {
            this.setTitle(
                this.translateService.getDynamicTranslation("Edit Template"),
                false,
            );
        }
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(),
            sampleSurveyTypes: this.sampleSurveyTypeService.getAll(),
        }).subscribe({
            next: (result) => {
                this.entity = result.entity;
                this.organizationLevels = result.organizationLevels;
                this.sampleSurveyTypes = result.sampleSurveyTypes;
                this.initModals();
                this.initFields();
                this.isFormLoading = false;
                this.initTemplates();
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
        this.fetchEmployees();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getNewObject(): SampleSurvey {
        return new SampleSurvey();
    }

    validate(): boolean {
        return true;
    }

    initModals() {
        if (this.entity.surveyPlannedStartDate) {
            this.entity.surveyPlannedStartDate =
                this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                    this.entity.surveyPlannedStartDate,
                );
        }
        if (this.entity.surveyPlannedEndDate) {
            this.entity.surveyPlannedEndDate =
                this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                    this.entity.surveyPlannedEndDate,
                );
        }
        if (this.entity.fileAttachmentIds?.length) {
            const requests = this.entity.fileAttachmentIds.map((id) =>
                this.fileAttachmentService.getById(id),
            );

            forkJoin(requests).subscribe({
                next: (responses) => {
                    this.attachedFiles = responses;
                    const attachmentField = this.fields.find(
                        (field) =>
                            field?.property ===
                            nameOf<SampleSurvey>((x) => x.fileAttachmentIds),
                    );
                    if (attachmentField) {
                        attachmentField.options = this.attachedFiles;
                    }
                },
                error: (err) => {
                    this.toastService.showError(err);
                },
            });
        } else {
            this.entity.fileAttachmentIds = [];
        }
    }

    initFields(): void {
        const surveySearchfields = [
            nameOf<SampleSurveyType>((x) => x.code),
            nameOf<SampleSurveyType>((x) => x.name),
            nameOf<SampleSurveyType>((x) => x.description),
        ];
        const fields: FormField[] = [
            new TextFormField({
                property: nameOf<SampleSurvey>((x) => x.name),
                name: "Survey Name",
                columnSpan: new ColumnSpan(1, 1),
            }),
            new SingleSelectionFormField({
                property: nameOf<SampleSurvey>((x) => x.surveyTypeId),
                name: "Survey Type",
                options: this.sampleSurveyTypes,
                columnSpan: new ColumnSpan(1, 1),
                searchKeys: surveySearchfields,
                optionTemplateFields: surveySearchfields,
                optionTemplateLabelMap: {
                    code: "Code",
                    name: "Name",
                    description: "Description",
                },
                selectionTemplate: (opt: FormField) => {
                    const entityValue = (
                        this.entity as unknown as Record<string, unknown>
                    )[opt.property];
                    return `${opt.options.find((res) => res.id == entityValue)?.name}`;
                },
            }),
            new TextFormField({
                property: nameOf<SampleSurvey>((x) => x.surveyCode),
                name: "Survey Code",
                columnSpan: new ColumnSpan(1, 1),
                required: true,
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<SampleSurvey>((x) => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: "Organization Level",
                columnSpan: new ColumnSpan(1, 1),
                required: true,
            }),
            new SingleSelectionFormField({
                property: nameOf<SampleSurvey>((x) => x.requestedById),
                options: this.employees,
                name: "Requested By",
                cssClass: "custom-search-employee",
                onSearchChange: (event: string, field: FormField) => {
                    this.updateEntities(event, field);
                },
                optionTemplateFields: [
                    nameOf<CustomEntity>((x) => x.name),
                    nameOf<CustomEntity>((x) => x.organizationLevelName),
                ],
                optionTemplateLabelMap: { ...EMPLOYEE_BASE_LABEL_MAP },
                selectionTemplate: (opt: FormField) => {
                    const selectedId = (
                        this.entity as unknown as Record<string, unknown>
                    )[opt.property];
                    const property = opt.options.find(
                        (res) => res.id == selectedId,
                    );
                    return (
                        property?.name +
                        " - " +
                        this.shownFields
                            .map(
                                (field) =>
                                    (
                                        property as unknown as Record<
                                            string,
                                            unknown
                                        >
                                    )?.[field],
                            )
                            .join(" ")
                    );
                },
                columnSpan: new ColumnSpan(1, 1),
                required: false,
                searchComponent: EndpointsTableComponent,
                additonalSearchData: { coId: this.coId },
            }),
            new DateFormField({
                property: nameOf<SampleSurvey>((x) => x.surveyPlannedStartDate),
                name: "Start Date",
                columnSpan: new ColumnSpan(1, 1),
            }),
            new DateFormField({
                property: nameOf<SampleSurvey>((x) => x.surveyPlannedEndDate),
                name: "End Date",
                columnSpan: new ColumnSpan(1, 1),
            }),
            new TextAreaFormField({
                property: nameOf<SampleSurvey>((x) => x.surveyDescription),
                name: "Survey Description",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
            }),
            new FileAttachmentsField({
                property: nameOf<SampleSurvey>((x) => x.fileAttachmentIds),
                name: "Attached  Files",
                columnSpan: new ColumnSpan(1, 1),
                options: this.attachedFiles,
                onClick: (id: number) => this.updateAttachment(id),
                additionalLabel: () =>
                    this.translateService.getDynamicTranslation(
                        "+ Add Attachment (up to 5) :",
                    ),
                required: false,
            }),
        ];
        this.fields = this.isTemplateEdit
            ? this.toTemplateEditFields(fields)
            : fields;
        this.setColumnDetails();
    }

    updateAttachment(id: string | number) {
        if (id === 0) this.openAttachmentForm();
        else {
            this.entity.fileAttachmentIds =
                this.entity.fileAttachmentIds.filter(
                    (attachmentId: string) => attachmentId !== id,
                );
            this.attachedFiles = this.attachedFiles.filter(
                (attachment: FileAttachment) => attachment.id !== id,
            );
            const field = this.fields.find(
                (f) =>
                    f?.property ===
                    nameOf<SampleSurvey>((x) => x.fileAttachmentIds),
            );
            if (field) {
                field.options = this.attachedFiles;
                this.onFieldValueChanged(field);
            }
        }
    }

    openAttachmentForm() {
        this.dialog
            .open(AttachmentsDialogComponent, {
                data: {
                    allowedFileTypes: ["jpg", "jpeg", "png", "pdf"],
                    maxFileSizeMB: 50,
                    assetType:
                        AssetTypeEnum.IHAttachments /* Need to update for sample survey */,
                    organizationLevelId: this.entity.organizationLevelId,
                },
                width: "460px",
                panelClass: "attachments-dialog",
                autoFocus: false,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    if (result) {
                        this.entity.fileAttachmentIds.push(result.id);
                        this.attachedFiles.push(result);
                        const field = this.fields.find(
                            (f) =>
                                f?.property ===
                                nameOf<SampleSurvey>((x) => x.fileAttachmentIds),
                        );
                        if (field) {
                            field.options = [...this.attachedFiles];
                            this.onFieldValueChanged(field);
                        }
                    }
                },
            });
    }

    fetchEmployees() {
        const handleError = (error: unknown) => {
            this.isFormLoading = false;
            this.toastService.showError(error);
            this.customObjectsList = [];
            this.employeesReady$.next();
        };

        this.customObjectService.getAll().pipe(
            switchMap((typeLookups) => {
                const employeeType = typeLookups.find(
                    (t) => t.name === "Employee",
                );
                if (!employeeType) {
                    this.employees = [];
                    return EMPTY;
                }
                this.coId = employeeType.id;

                return this.customEntityService
                    .get(
                        "",
                        "id",
                        "asc",
                        0,
                        100,
                        "",
                        new HttpHeaders({ coid: this.coId }),
                    ).pipe(
                        switchMap((res) => {
                            this.employees = res.data.filter(
                                (e) => e.customObjectId === employeeType.id,
                            );
                            const requestField = this.fields.find(
                                (field) =>
                                    field.property ==
                                    nameOf<SampleSurvey>(
                                        (res) => res.requestedById,
                                    ),
                            );
                            if (!requestField) {
                                return EMPTY;
                            }
                            requestField.options = this.employees;
                            return this.customObjectService
                                .getCustomObjectFields(employeeType.id).pipe(
                                    tap((fields) => {
                                        this.customObjectsList = fields.filter(
                                            (f) =>
                                                isCustomFieldKey(f.property),
                                        );
                                        this.shownFields = this.customObjectsList
                                            .filter((f) =>
                                                [
                                                    CustomFieldTypeEnum.HrisFirstName,
                                                    CustomFieldTypeEnum.HrisLastName,
                                                ].includes(f.type),
                                            )
                                            .map((f) => f.property);
                                        this.isFormLoading = false;
                                        const mappedKeys = this.customObjectsList
                                            .filter(
                                                (obj) =>
                                                    obj &&
                                                    typeof obj.property ===
                                                        "string",
                                            )
                                            .map((obj) => obj.property);
                                        requestField.searchKeys = [
                                            ...mappedKeys,
                                            "name",
                                        ];
                                        const config = buildEmployeeOptionConfig(
                                            this.customObjectsList,
                                        );
                                        requestField.optionTemplateFields =
                                            config.optionTemplateFields;
                                        requestField.optionTemplateLabelMap =
                                            config.optionTemplateLabelMap;
                                        if (
                                            this.entity?.requestedById &&
                                            this.entity.endPoint?.name
                                        ) {
                                            this.updateEntities(
                                                this.entity.endPoint.name,
                                                requestField,
                                            );
                                        }
                                    })
                                );
                        })
                    );
            })
        ).subscribe({
            error: handleError,
            complete: () => this.employeesReady$.next(),
        });
    }

    updateEntities(filter: string, employeeField: FormField) {
        this.customEntityService
            .get(
                filter,
                "id",
                "asc",
                0,
                100,
                "",
                new HttpHeaders({ coid: this.coId }),
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    const curr = this.entity.requestedById
                        ? (employeeField.options.find(
                              (opt) => opt.id === this.entity.requestedById,
                          ) as CustomEntity)
                        : null;
                    if (curr) {
                        res.data.push(curr);
                    }
                    this.employees = res.data;
                    if (this.fields) {
                        if (employeeField)
                            employeeField.options = this.employees;
                    }
                },
            });
    }

    onBeforeSave(model: SampleSurvey): void {
        if (this.isTemplateEdit) {
            return;
        }
        const { surveyPlannedStartDate, surveyPlannedEndDate } = model;
        model.surveyPlannedStartDate =
            this.dateFormatService.convertUserTimeZoneStringToUTCDateString(
                surveyPlannedStartDate,
            );
        model.surveyPlannedEndDate =
            this.dateFormatService.convertUserTimeZoneStringToUTCDateString(
                surveyPlannedEndDate,
            );
    }

    override saveAction(model: SampleSurvey) {
        if (this.isTemplateEdit && this.editingTemplate) {
            return this.surveyTemplateService.update({
                ...this.editingTemplate,
                settings: this.buildTemplateSettings(),
            });
        }
        return super.saveAction(model);
    }

    override afterSaveAction(response: unknown): void {
        if (!this.isTemplateEdit) {
            super.afterSaveAction(response);
            return;
        }
        this.isLoading = false;
        this.templateBaseline = JSON.stringify(this.buildTemplateSettings());
        this.isTemplateDirtyState = false;
        this.toastService.showSuccess("Template updated successfully", true);
        this.closeDialog();
    }

    override close(): void {
        if (!this.isTemplateEdit || !this.isTemplateDirty()) {
            super.close();
            return;
        }
        this.openConfirmDialog({
            title: "Discard changes?",
            message:
                "You have unsaved changes to this template. Do you want to discard them?",
            primaryButtonText: "Discard",
            secondaryButtonText: "Keep editing",
        })
            .pipe(filter((confirmed) => confirmed))
            .subscribe({ next: () => super.close() });
    }

    onFieldValueChanged(field: FormField): void {
        const property = field.property;
        if (
            this.canUseTemplates &&
            this.templateFilledFields.has(property) &&
            !this.templateEditedFields.has(property)
        ) {
            this.templateEditedFields.add(property);
            this.rebuildTemplateFieldStates();
        }
        if (this.isTemplateEdit) {
            this.isTemplateDirtyState = this.isTemplateDirty();
        }
        this.updateFieldVisibility();
    }

    onTemplateSelected(templateId: string | null): void {
        if (!templateId) {
            this.clearTemplateFields();
            return;
        }
        this.applyTemplate(
            this.surveyTemplateService.getById(templateId),
        ).subscribe();
    }

    clearTemplateFields(): void {
        this.templateSelectionRequestId++;
        this.resetTemplateFilledFields();
        this.templateFilledFields.clear();
        this.templateEditedFields.clear();
        this.templateFieldStates = new Map<string, TemplateFieldState>();
        this.setAppliedTemplate(null);
        this.updateFieldVisibility();
    }

    onSaveTemplateClick(): void {
        if (this.isSavingTemplate) {
            return;
        }
        this.isSavingTemplate = true;
        switch (this.saveTemplateMode) {
            case SaveTemplateMode.Update:
                this.updateAppliedTemplate();
                return;
            case SaveTemplateMode.ConfirmSaveAsNew:
                this.confirmSaveAppliedTemplateAsNew();
                return;
            default:
                this.openSaveAsTemplateDialog();
        }
    }

    private initTemplates(): void {
        if (this.isTemplateEdit) {
            this.loadTemplateForEdit();
        } else if (this.canUseTemplates) {
            this.loadTemplates();
        }
    }

    private toTemplateEditFields(fields: FormField[]): FormField[] {
        const templatable = fields.filter((field) =>
            SURVEY_TEMPLATE_FIELDS.some((key) => key === field.property),
        );
        templatable.forEach((field) => {
            field.required = false;
        });
        return templatable;
    }

    private findField(property: string): FormField | undefined {
        return this.fields.find((field) => field?.property === property);
    }

    private loadTemplates(): void {
        forkJoin({
            templates: this.surveyTemplateService
                .getAll()
                .pipe(
                    catchError((error) =>
                        this.toastAndFallback<Lookup[]>(error, []),
                    ),
                ),
            defaultTemplate: this.surveyTemplateService
                .getDefault()
                .pipe(
                    catchError((error) =>
                        this.toastAndFallback<SurveyTemplate | null>(
                            error,
                            null,
                        ),
                    ),
                ),
        })
            .pipe(
                tap(({ templates }) => {
                    this.availableTemplates = templates.map((template) => ({
                        id: template.id,
                        name: template.name,
                    }));
                }),
                map(({ defaultTemplate }) => defaultTemplate),
                filter(
                    (defaultTemplate): defaultTemplate is SurveyTemplate =>
                        !!defaultTemplate,
                ),
                switchMap((defaultTemplate) =>
                    this.applyTemplate(of({ ...defaultTemplate, isDefault: true })),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    private loadTemplateForEdit(): void {
        this.isFormLoading = true;
        this.employeesReady$
            .pipe(
                take(1),
                switchMap(() =>
                    this.surveyTemplateService.getById(this.editTemplateId),
                ),
                switchMap((template) =>
                    this.resolveTemplateSettings(template.settings).pipe(
                        map((resolved) => ({ template, resolved })),
                    ),
                ),
                finalize(() => {
                    this.isFormLoading = false;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: ({ template, resolved }) => {
                    this.editingTemplate = template;
                    this.applyResolvedSettings(resolved);
                    this.templateBaseline = JSON.stringify(
                        this.buildTemplateSettings(),
                    );
                    this.updateFieldVisibility();
                },
                error: (error) => this.toastService.showError(error),
            });
    }

    private toastAndFallback<T>(error: unknown, fallback: T): Observable<T> {
        this.toastService.showError(error);
        return of(fallback);
    }

    private applyTemplate(template$: Observable<SurveyTemplate>): Observable<void> {
        const requestId = ++this.templateSelectionRequestId;
        return template$.pipe(
            switchMap((template) =>
                this.employeesReady$.pipe(
                    take(1),
                    switchMap(() =>
                        this.resolveTemplateSettings(template.settings),
                    ),
                    map((resolved) => ({ template, resolved })),
                ),
            ),
            filter(() => requestId === this.templateSelectionRequestId),
            tap(({ template, resolved }) => {
                this.resetTemplateFilledFields();
                this.applyResolvedSettings(resolved);
                this.setAppliedTemplate(template);
                this.updateFieldVisibility();
            }),
            map(() => undefined),
            catchError((error) => {
                if (requestId === this.templateSelectionRequestId) {
                    this.toastService.showError(error);
                }
                return EMPTY;
            }),
            takeUntil(this.destroy$),
        );
    }

    private resolveTemplateSettings(
        settings: SurveyTemplateSettings | null | undefined,
    ): Observable<ResolvedSurveyTemplate> {
        const source = settings ?? {};
        const fileIds = source.fileAttachmentIds ?? [];
        return forkJoin({
            employee: source.requestedById
                ? this.resolveEmployee(source.requestedById)
                : of(null),
            files: fileIds.length
                ? this.fileAttachmentService
                      .get(
                          "",
                          "id",
                          "asc",
                          0,
                          fileIds.length,
                          JSON.stringify({
                              [nameOf<FileAttachment>((x) => x.id)]:
                                  fileIds.join(","),
                          }),
                      )
                      .pipe(
                          map((response) => response.data),
                          catchError((error) =>
                              this.toastAndFallback<FileAttachment[]>(
                                  error,
                                  [],
                              ),
                          ),
                      )
                : of<FileAttachment[]>([]),
        }).pipe(
            map(({ employee, files }) => ({
                employee,
                files,
                settings: {
                    name: source.name || null,
                    surveyCode: source.surveyCode || null,
                    surveyPlannedStartDate: this.toUserTimeZoneDate(
                        source.surveyPlannedStartDate,
                    ),
                    surveyPlannedEndDate: this.toUserTimeZoneDate(
                        source.surveyPlannedEndDate,
                    ),
                    surveyTypeId: this.sampleSurveyTypes.some(
                        (type) => type.id === source.surveyTypeId,
                    )
                        ? source.surveyTypeId
                        : null,
                    organizationLevelId: this.organizationLevels.some(
                        (level) => level.id === source.organizationLevelId,
                    )
                        ? source.organizationLevelId
                        : null,
                    requestedById: employee ? source.requestedById : null,
                    surveyDescription: source.surveyDescription || null,
                    fileAttachmentIds: files.length
                        ? files.map((file) => file.id)
                        : null,
                },
            })),
        );
    }

    private resolveEmployee(id: string): Observable<ILookup | null> {
        const known = this.findField(this.requestedByKey)?.options.find(
            (option) => option.id === id,
        );
        return known
            ? of(known)
            : this.customEntityService
                  .getCustomEntityQuietly(id, this.coId)
                  .pipe(catchError(() => of(null)));
    }

    private applyResolvedSettings(resolved: ResolvedSurveyTemplate): void {
        const applied = SURVEY_TEMPLATE_FIELDS.filter(
            (key) =>
                !this.isEmptyTemplateValue(resolved.settings[key]) &&
                this.isEmptyTemplateValue(this.entity[key]),
        );
        applied.forEach((key) => {
            const value = resolved.settings[key];
            Object.assign(this.entity, {
                [key]: Array.isArray(value) ? [...value] : value,
            });
            if (this.canUseTemplates) {
                this.templateFilledFields.add(key);
                this.templateEditedFields.delete(key);
            }
        });
        if (applied.includes(this.requestedByKey) && resolved.employee) {
            this.addEmployeeOption(resolved.employee);
        }
        if (applied.includes(this.attachmentsKey)) {
            this.setAttachedFiles(resolved.files);
        }
        this.rebuildTemplateFieldStates();
    }

    private addEmployeeOption(employee: ILookup): void {
        const field = this.findField(this.requestedByKey);
        if (!field || field.options.some((option) => option.id === employee.id)) {
            return;
        }
        this.employees = [...field.options, employee];
        field.options = this.employees;
    }

    private setAttachedFiles(files: FileAttachment[]): void {
        this.attachedFiles = files;
        const field = this.findField(this.attachmentsKey);
        if (field) {
            field.options = files;
        }
    }

    private resetTemplateFilledFields(): void {
        SURVEY_TEMPLATE_FIELDS.filter(
            (key) =>
                this.templateFilledFields.has(key) &&
                !this.templateEditedFields.has(key),
        ).forEach((key) => {
            const isAttachments = key === this.attachmentsKey;
            Object.assign(this.entity, { [key]: isAttachments ? [] : null });
            if (isAttachments) {
                this.setAttachedFiles([]);
            }
            this.templateFilledFields.delete(key);
        });
    }

    private rebuildTemplateFieldStates(): void {
        const states = new Map<string, TemplateFieldState>();
        this.templateFilledFields.forEach((property) => {
            states.set(
                property,
                this.templateEditedFields.has(property)
                    ? "edited"
                    : "from-template",
            );
        });
        this.templateFieldStates = states;
    }

    private isEmptyTemplateValue(value: unknown): boolean {
        return (
            value == null ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
        );
    }

    private toUserTimeZoneDate(utcDate: string | null | undefined): string | null {
        return utcDate
            ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                  utcDate,
              )
            : null;
    }

    private buildTemplateSettings(): SurveyTemplateSettings {
        return SURVEY_TEMPLATE_FIELDS.reduce<SurveyTemplateSettings>(
            (settings, key) => {
                const value = this.entity[key];
                if (this.isEmptyTemplateValue(value)) {
                    return { ...settings, [key]: null };
                }
                if (SURVEY_TEMPLATE_DATE_FIELDS.includes(key)) {
                    return {
                        ...settings,
                        [key]: this.dateFormatService.convertUserTimeZoneStringToUTCDateString(
                            String(value),
                        ),
                    };
                }
                return {
                    ...settings,
                    [key]: Array.isArray(value) ? [...value] : value,
                };
            },
            {},
        );
    }

    private isTemplateDirty(): boolean {
        return (
            this.templateBaseline != null &&
            JSON.stringify(this.buildTemplateSettings()) !==
                this.templateBaseline
        );
    }

    private setAppliedTemplate(template: SurveyTemplate | null): void {
        this.appliedTemplate = template;
        this.selectedTemplateId = template?.id ?? null;
        this.isDefaultTemplateBannerVisible = !!template?.isDefault;
        this.saveTemplateMode = !template
            ? SaveTemplateMode.Create
            : template.canManage
              ? SaveTemplateMode.Update
              : SaveTemplateMode.ConfirmSaveAsNew;
        this.saveTemplateLabel = SAVE_TEMPLATE_MODE_LABELS[this.saveTemplateMode];
    }

    private updateAppliedTemplate(): void {
        if (!this.appliedTemplate) {
            this.isSavingTemplate = false;
            return;
        }
        const updatedTemplate: SurveyTemplate = {
            ...this.appliedTemplate,
            settings: this.buildTemplateSettings(),
        };
        this.surveyTemplateService
            .update(updatedTemplate)
            .pipe(
                finalize(() => {
                    this.isSavingTemplate = false;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: () => {
                    this.appliedTemplate = updatedTemplate;
                    this.templateEditedFields.clear();
                    this.rebuildTemplateFieldStates();
                    this.toastService.showSuccess(
                        "Template updated successfully",
                        true,
                    );
                },
                error: (error) => this.toastService.showError(error),
            });
    }

    private confirmSaveAppliedTemplateAsNew(): void {
        this.openConfirmDialog({
            title: "Save as New Template",
            message:
                "You can't update this template because you don't own it. Save your changes as a new template instead?",
            primaryButtonText: "Save as new",
            secondaryButtonText: "Cancel",
        }).subscribe({
            next: (confirmed) => {
                if (confirmed) {
                    this.openSaveAsTemplateDialog();
                } else {
                    this.isSavingTemplate = false;
                }
            },
        });
    }

    private openSaveAsTemplateDialog(): void {
        const data: TemplateSaveDialogData = {
            dataOrganizationLevelId: this.entity.organizationLevelId ?? null,
        };
        this.dialog
            .open(TemplateSaveDialogComponent, {
                data,
                width: "440px",
                disableClose: true,
            })
            .afterClosed()
            .pipe(
                filter(
                    (result): result is TemplateSaveDialogResult => !!result,
                ),
                switchMap((result) => {
                    const payload: CreatePayload<SurveyTemplate> = {
                        name: result.name,
                        isShared: result.isShared,
                        isDefault: false,
                        organizationLevelId:
                            result.organizationLevelId ??
                            this.entity.organizationLevelId,
                        canManage: true,
                        settings: this.buildTemplateSettings(),
                    };
                    return this.surveyTemplateService.create(payload);
                }),
                finalize(() => {
                    this.isSavingTemplate = false;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (created) => {
                    this.toastService.showSuccess(
                        "Template saved successfully",
                        true,
                    );
                    this.availableTemplates = [
                        ...this.availableTemplates,
                        { id: created.id, name: created.name },
                    ];
                    this.templateFilledFields.clear();
                    this.templateEditedFields.clear();
                    this.rebuildTemplateFieldStates();
                    this.setAppliedTemplate({ ...created, canManage: true });
                },
                error: (error) => this.toastService.showError(error),
            });
    }

    private openConfirmDialog(options: {
        title: string;
        message: string;
        primaryButtonText: string;
        secondaryButtonText: string;
    }): Observable<boolean> {
        const data: ConfirmDialogData = {
            title: this.translateService.getDynamicTranslation(options.title),
            message: this.translateService.getDynamicTranslation(
                options.message,
            ),
            primaryButtonText: this.translateService.getDynamicTranslation(
                options.primaryButtonText,
            ),
            secondaryButtonText: this.translateService.getDynamicTranslation(
                options.secondaryButtonText,
            ),
            successMessage: "",
            errorMessage: "",
        };
        return this.dialog
            .open(ConfirmDialogComponent, { data, width: "440px" })
            .afterClosed()
            .pipe(
                map((result) => result === true),
                takeUntil(this.destroy$),
            );
    }
}
